/**
 * Save/Load Menu - UI for managing game saves
 */

import { storageManager, type SaveSlot } from '@/core/save/StorageManager';
import { autoSave } from '@/core/save/AutoSave';
import type { SaveData } from '@/core/save/SaveData';
import { MAX_SAVE_SLOTS } from '@/core/save/SaveData';
import { FACTIONS } from '@/game/factions/FactionData';

export interface SaveLoadMenuCallbacks {
  onSave: (slot: number, name: string) => Promise<boolean>;
  onLoad: (saveData: SaveData) => void;
  onReturnToMenu: () => void;
}

export class SaveLoadMenu {
  private container: HTMLElement;
  private visible: boolean = false;
  private mode: 'save' | 'load' = 'save';
  private callbacks: SaveLoadMenuCallbacks | null = null;
  private slots: SaveSlot[] = [];

  constructor() {
    this.container = document.createElement('div');
    this.container.id = 'save-load-menu';
    this.container.innerHTML = `
      <style>
        #save-load-menu {
          position: fixed;
          left: 50%;
          top: 50%;
          transform: translate(-50%, -50%);
          z-index: 250;
          font: 14px/1.5 'Noto Serif SC', 'SimSun', serif;
          color: #f5e6d3;
          user-select: none;
          min-width: 420px;
          display: none;
        }
        #save-load-menu.visible {
          display: block;
        }

        .save-menu-wrapper {
          background:
            linear-gradient(145deg, rgba(74,55,40,0.95) 0%, rgba(45,31,21,0.98) 100%);
          border: 3px solid #8b6914;
          border-radius: 8px;
          box-shadow:
            inset 0 2px 4px rgba(255,220,150,0.15),
            0 8px 32px rgba(0,0,0,0.6);
          position: relative;
          overflow: hidden;
        }

        .save-menu-wrapper::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><circle cx="20" cy="20" r="1" fill="%23d4a84b" opacity="0.1"/><circle cx="80" cy="40" r="0.8" fill="%23d4a84b" opacity="0.08"/><circle cx="40" cy="70" r="1.2" fill="%23d4a84b" opacity="0.06"/></svg>');
          pointer-events: none;
        }

        /* Dragon corner ornaments */
        .save-menu-corner {
          position: absolute;
          width: 36px;
          height: 36px;
          background-image: url('/images/ui/decorations/corner-ornaments.png');
          background-size: 200% 200%;
          z-index: 1;
          filter: drop-shadow(0 2px 3px rgba(0,0,0,0.4));
          pointer-events: none;
        }
        .save-menu-corner.tl { top: -4px; left: -4px; background-position: 0% 0%; }
        .save-menu-corner.tr { top: -4px; right: -4px; background-position: 100% 0%; }
        .save-menu-corner.bl { bottom: -4px; left: -4px; background-position: 0% 100%; }
        .save-menu-corner.br { bottom: -4px; right: -4px; background-position: 100% 100%; }

        #save-load-menu .menu-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 16px 20px;
          border-bottom: 2px solid rgba(139,105,20,0.5);
          background: linear-gradient(180deg, rgba(139,105,20,0.2) 0%, transparent 100%);
        }
        #save-load-menu .menu-title {
          font-weight: 700;
          font-size: 18px;
          color: #d4a84b;
          letter-spacing: 4px;
          text-shadow: 0 2px 4px rgba(0,0,0,0.5);
        }
        #save-load-menu .close-btn {
          background: none;
          border: none;
          color: #d4a84b;
          cursor: pointer;
          font-size: 24px;
          padding: 0 4px;
          opacity: 0.8;
          transition: opacity 0.2s;
        }
        #save-load-menu .close-btn:hover {
          opacity: 1;
        }

        #save-load-menu .menu-content {
          padding: 16px 20px;
        }

        #save-load-menu .mode-tabs {
          display: flex;
          gap: 8px;
          margin-bottom: 16px;
        }
        #save-load-menu .mode-tab {
          flex: 1;
          padding: 10px 16px;
          background: rgba(139,105,20,0.2);
          border: 1px solid rgba(139,105,20,0.4);
          border-radius: 4px;
          color: #c9b896;
          cursor: pointer;
          font: inherit;
          font-size: 14px;
          font-weight: 600;
          transition: all 0.2s;
        }
        #save-load-menu .mode-tab:hover {
          background: rgba(139,105,20,0.3);
          color: #f5e6d3;
        }
        #save-load-menu .mode-tab.active {
          background: linear-gradient(180deg, rgba(212,168,75,0.4) 0%, rgba(139,105,20,0.3) 100%);
          border-color: #d4a84b;
          color: #d4a84b;
        }

        #save-load-menu .slots-list {
          display: flex;
          flex-direction: column;
          gap: 8px;
          max-height: 280px;
          overflow-y: auto;
        }
        #save-load-menu .slots-list::-webkit-scrollbar {
          width: 6px;
        }
        #save-load-menu .slots-list::-webkit-scrollbar-track {
          background: rgba(0,0,0,0.2);
          border-radius: 3px;
        }
        #save-load-menu .slots-list::-webkit-scrollbar-thumb {
          background: rgba(139,105,20,0.5);
          border-radius: 3px;
        }

        #save-load-menu .slot-item {
          display: flex;
          align-items: center;
          padding: 12px 14px;
          background: rgba(0,0,0,0.2);
          border: 1px solid rgba(139,105,20,0.3);
          border-radius: 4px;
          cursor: pointer;
          transition: all 0.2s;
        }
        #save-load-menu .slot-item:hover {
          background: rgba(139,105,20,0.15);
          border-color: rgba(139,105,20,0.5);
        }
        #save-load-menu .slot-item.empty {
          opacity: 0.6;
        }
        #save-load-menu .slot-info {
          flex: 1;
        }
        #save-load-menu .slot-name {
          font-weight: 600;
          margin-bottom: 4px;
          color: #f5e6d3;
        }
        #save-load-menu .slot-meta {
          font-size: 11px;
          color: #9e8b6e;
        }
        #save-load-menu .slot-actions {
          display: flex;
          gap: 8px;
        }
        #save-load-menu .slot-btn {
          padding: 6px 14px;
          background: rgba(139,105,20,0.3);
          border: 1px solid rgba(139,105,20,0.5);
          border-radius: 3px;
          color: #c9b896;
          cursor: pointer;
          font: inherit;
          font-size: 12px;
          transition: all 0.2s;
        }
        #save-load-menu .slot-btn:hover {
          background: rgba(139,105,20,0.5);
          color: #f5e6d3;
        }
        #save-load-menu .slot-btn:disabled {
          opacity: 0.4;
          cursor: not-allowed;
        }
        #save-load-menu .slot-btn.primary {
          background: linear-gradient(180deg, rgba(212,168,75,0.4) 0%, rgba(139,105,20,0.3) 100%);
          border-color: #d4a84b;
          color: #d4a84b;
        }
        #save-load-menu .slot-btn.primary:hover {
          background: linear-gradient(180deg, rgba(212,168,75,0.5) 0%, rgba(139,105,20,0.4) 100%);
        }
        #save-load-menu .slot-btn.danger {
          background: rgba(139,60,60,0.3);
          border-color: rgba(180,80,80,0.5);
          color: #e8a0a0;
        }
        #save-load-menu .slot-btn.danger:hover {
          background: rgba(139,60,60,0.5);
        }

        #save-load-menu .menu-footer {
          padding: 12px 20px;
          border-top: 1px solid rgba(139,105,20,0.3);
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        #save-load-menu .autosave-toggle {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 12px;
          color: #9e8b6e;
          cursor: pointer;
        }
        #save-load-menu .autosave-toggle input {
          width: 14px;
          height: 14px;
          accent-color: #d4a84b;
        }

        #save-load-menu .menu-btn {
          padding: 8px 16px;
          background: rgba(100,60,60,0.3);
          border: 1px solid rgba(150,80,80,0.5);
          border-radius: 4px;
          color: #d4a0a0;
          cursor: pointer;
          font: inherit;
          font-size: 12px;
          transition: all 0.2s;
        }
        #save-load-menu .menu-btn:hover {
          background: rgba(120,60,60,0.5);
          color: #f5d0d0;
        }
      </style>
      <div class="save-menu-wrapper">
        <div class="save-menu-corner tl"></div>
        <div class="save-menu-corner tr"></div>
        <div class="save-menu-corner bl"></div>
        <div class="save-menu-corner br"></div>

        <div class="menu-header">
          <span class="menu-title">存 档 管 理</span>
          <button class="close-btn">&times;</button>
        </div>

        <div class="menu-content">
          <div class="mode-tabs">
            <button class="mode-tab active" data-mode="save">保存游戏</button>
            <button class="mode-tab" data-mode="load">读取游戏</button>
          </div>
          <div class="slots-list"></div>
        </div>

        <div class="menu-footer">
          <label class="autosave-toggle">
            <input type="checkbox" id="autosave-enabled" checked>
            <span>启用自动存档 (每5分钟)</span>
          </label>
          <button class="menu-btn" id="return-to-menu-btn">返回主菜单</button>
        </div>
      </div>
    `;

    document.body.appendChild(this.container);
    this.setupEvents();
  }

  setCallbacks(callbacks: SaveLoadMenuCallbacks): void {
    this.callbacks = callbacks;
  }

  private setupEvents(): void {
    const closeBtn = this.container.querySelector('.close-btn');
    closeBtn?.addEventListener('click', () => this.hide());

    const modeTabs = this.container.querySelectorAll('.mode-tab');
    modeTabs.forEach(tab => {
      tab.addEventListener('click', () => {
        this.mode = (tab as HTMLElement).dataset.mode as 'save' | 'load';
        this.render();
      });
    });

    const autosaveCheckbox = this.container.querySelector('#autosave-enabled') as HTMLInputElement;
    autosaveCheckbox?.addEventListener('change', () => {
      autoSave.setEnabled(autosaveCheckbox.checked);
    });

    const returnBtn = this.container.querySelector('#return-to-menu-btn');
    returnBtn?.addEventListener('click', () => {
      if (confirm('确定要返回主菜单吗？未保存的进度将会丢失。')) {
        this.hide();
        this.callbacks?.onReturnToMenu();
      }
    });
  }

  async show(mode: 'save' | 'load' = 'save'): Promise<void> {
    this.mode = mode;
    this.visible = true;
    this.container.classList.add('visible');

    // Load slots
    await storageManager.init();
    this.slots = await storageManager.getAllSlots();

    this.render();
  }

  hide(): void {
    this.visible = false;
    this.container.classList.remove('visible');
  }

  isVisible(): boolean {
    return this.visible;
  }

  toggle(mode: 'save' | 'load' = 'save'): void {
    if (this.visible) {
      this.hide();
    } else {
      this.show(mode);
    }
  }

  private render(): void {
    // Update tabs
    const tabs = this.container.querySelectorAll('.mode-tab');
    tabs.forEach(tab => {
      tab.classList.toggle('active', (tab as HTMLElement).dataset.mode === this.mode);
    });

    // Update slots list
    const slotsEl = this.container.querySelector('.slots-list');
    if (!slotsEl) return;

    slotsEl.innerHTML = this.slots.map((slot, index) => {
      const isEmpty = slot.isEmpty || !slot.data;
      const data = slot.data;

      let metaText = '空槽位';
      if (data) {
        const date = new Date(data.timestamp);
        const faction = FACTIONS[data.playerFaction];
        const time = Math.floor(data.gameTime / 60);
        metaText = `${faction?.name || data.playerFaction} | ${time}分钟 | ${date.toLocaleString()}`;
      }

      return `
        <div class="slot-item ${isEmpty ? 'empty' : ''}" data-slot="${index}">
          <div class="slot-info">
            <div class="slot-name">${data?.name || `存档 ${index + 1}`}</div>
            <div class="slot-meta">${metaText}</div>
          </div>
          <div class="slot-actions">
            ${this.mode === 'save' ? `
              <button class="slot-btn primary" data-action="save" data-slot="${index}">保存</button>
            ` : `
              <button class="slot-btn primary" data-action="load" data-slot="${index}" ${isEmpty ? 'disabled' : ''}>读取</button>
            `}
            ${!isEmpty ? `
              <button class="slot-btn danger" data-action="delete" data-slot="${index}">删除</button>
            ` : ''}
          </div>
        </div>
      `;
    }).join('');

    // Add button handlers
    const buttons = slotsEl.querySelectorAll('.slot-btn');
    buttons.forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const action = (btn as HTMLElement).dataset.action;
        const slot = parseInt((btn as HTMLElement).dataset.slot || '0');
        this.handleAction(action!, slot);
      });
    });
  }

  private async handleAction(action: string, slot: number): Promise<void> {
    switch (action) {
      case 'save':
        if (this.callbacks) {
          const name = prompt('存档名称:', `存档 ${slot + 1}`) || `存档 ${slot + 1}`;
          const success = await this.callbacks.onSave(slot, name);
          if (success) {
            this.slots = await storageManager.getAllSlots();
            this.render();
          }
        }
        break;

      case 'load':
        const saveData = await storageManager.load(slot);
        if (saveData && this.callbacks) {
          this.callbacks.onLoad(saveData);
          this.hide();
        }
        break;

      case 'delete':
        if (confirm('确定要删除这个存档吗?')) {
          await storageManager.delete(slot);
          this.slots = await storageManager.getAllSlots();
          this.render();
        }
        break;
    }
  }

  dispose(): void {
    this.container.remove();
  }
}
